

import { io, Socket } from 'socket.io-client';
import * as mediasoupClient from 'mediasoup-client';
import type { types as mediasoupTypes } from 'mediasoup-client';

type Device = mediasoupTypes.Device;
type Transport = mediasoupTypes.Transport;
type Producer = mediasoupTypes.Producer;
type Consumer = mediasoupTypes.Consumer;
type RtpCapabilities = mediasoupTypes.RtpCapabilities;
type TransportOptions = mediasoupTypes.TransportOptions;
type DtlsParameters = mediasoupTypes.DtlsParameters;
type RtpParameters = mediasoupTypes.RtpParameters;
type MediaKind = mediasoupTypes.MediaKind;
type AppData = mediasoupTypes.AppData;

interface SocketResponse {
  success: boolean;
  error?: string;
}

interface CreateRoomResponse extends SocketResponse {
  roomId: string;
  peerId: string;
  rtpCapabilities: RtpCapabilities;
}

interface JoinRoomResponse extends SocketResponse {
  roomId: string;
  peerId: string;
  rtpCapabilities: RtpCapabilities;
  existingProducers: Array<{ producerId: string; peerId: string; kind: string; share?: boolean }>;
  existingPeers?: Array<{ peerId: string; displayName?: string | null }>;
}

interface CreateTransportResponse extends SocketResponse {
  transportParams: TransportOptions;
}

interface ProduceResponse extends SocketResponse {
  producerId: string;
}

interface ConsumeResponse extends SocketResponse {
  consumerId: string;
  producerId: string;
  kind: 'audio' | 'video';
  rtpParameters: mediasoupClient.types.RtpParameters;
  producerPeerId: string;
  share?: boolean;
}

// Event callback types
export type RemoteStreamCallback = (peerId: string, kind: 'audio' | 'video', stream: MediaStream) => void;
export type RemoteStreamRemovedCallback = (peerId: string, kind: 'audio' | 'video') => void;
export type PeerCallback = (peerId: string, displayName?: string) => void;
export type ScreenShareCallback = (peerId: string, stream: MediaStream) => void;
export type ScreenShareStoppedCallback = (peerId?: string) => void;
export type ConnectionStateCallback = (state: string) => void;

/**
 * MediaSoupClient class
 * Manages the entire MediaSoup client-side functionality
 */
class MediaSoupClient {
  private socket: Socket | null = null;
  private device: Device | null = null;
  private sendTransport: Transport | null = null;
  private recvTransport: Transport | null = null;
  private producers: Map<string, Producer> = new Map();
  private consumers: Map<string, Consumer> = new Map();
  private localStream: MediaStream | null = null;
  private screenStream: MediaStream | null = null;
  private screenProducer: Producer | null = null;
  private _pendingProducers: Array<{ producerId: string; peerId: string; kind: string; share?: boolean }> = [];

  public roomId: string | null = null;
  public peerId: string | null = null;
  // peerId -> display name, learned from joinRoom's existingPeers and peerJoined/newProducer events
  public peerNames: Map<string, string> = new Map();

  // Server URL - configurable
  private serverUrl: string;

  // Event callbacks
  public onRemoteStream: RemoteStreamCallback | null = null;
  public onRemoteStreamRemoved: RemoteStreamRemovedCallback | null = null;
  public onPeerJoined: PeerCallback | null = null;
  public onPeerLeft: PeerCallback | null = null;
  public onConnectionStateChange: ConnectionStateCallback | null = null;
  public onScreenShare: ScreenShareCallback | null = null;
  public onScreenShareStopped: ScreenShareStoppedCallback | null = null;

  constructor(serverUrl?: string) {
    this.serverUrl = serverUrl || 'http://localhost:3000';
  }

  /**
   * Connects to the signaling server
   */
  async connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.socket = io(this.serverUrl, {
        transports: ['polling', 'websocket'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });

      this.socket.on('connect', () => {
        console.log('[MediaSoupClient] Connected to signaling server');
        this._setupSocketListeners();
        resolve();
      });

      this.socket.on('connect_error', (error: Error) => {
        console.error('[MediaSoupClient] Connection error:', error);
        reject(new Error('Failed to connect to server'));
      });
    });
  }

  /**
   * Sets up socket event listeners for signaling
   */
  private _setupSocketListeners(): void {
    if (!this.socket) return;

    // New producer from another peer
    this.socket.on('newProducer', async ({ producerId, peerId, kind, share, displayName }: { producerId: string; peerId: string; kind: string; share?: boolean; displayName?: string }) => {
      console.log(`[MediaSoupClient] New producer from ${peerId}: ${kind} (share: ${share})`);
      if (displayName) this.peerNames.set(peerId, displayName);

      // If recv transport isn't ready yet, buffer for later consumption
      if (!this.recvTransport || !this.device) {
        console.log('[MediaSoupClient] Recv transport not ready, buffering producer for later');
        this._pendingProducers.push({ producerId, peerId, kind, share });
        return;
      }
      
      try {
        await this._consumeProducer(producerId, peerId, share);
      } catch (err) {
        console.error('[MediaSoupClient] Failed to consume new producer:', err);
      }
    });

    // Peer joined the room
    this.socket.on('peerJoined', ({ peerId, displayName }: { peerId: string; displayName?: string }) => {
      console.log(`[MediaSoupClient] Peer joined: ${peerId}`);
      if (displayName) this.peerNames.set(peerId, displayName);
      if (this.onPeerJoined) this.onPeerJoined(peerId, displayName);
    });

    // Peer left the room
    this.socket.on('peerLeft', ({ peerId }: { peerId: string }) => {
      console.log(`[MediaSoupClient] Peer left: ${peerId}`);
      // Remove consumers for this peer
      this.consumers.forEach((consumer, consumerId) => {
        if (consumer.appData?.producerPeerId === peerId) {
          const isShare = consumer.appData?.share as boolean;
          consumer.close();
          this.consumers.delete(consumerId);
          if (isShare && this.onScreenShareStopped) {
            this.onScreenShareStopped(peerId);
          } else if (this.onRemoteStreamRemoved) {
            this.onRemoteStreamRemoved(peerId, consumer.kind as 'audio' | 'video');
          }
        }
      });
      if (this.onPeerLeft) this.onPeerLeft(peerId);
    });

    // Consumer closed (producer stopped)
    this.socket.on('consumerClosed', ({ consumerId }: { consumerId: string }) => {
      const consumer = this.consumers.get(consumerId);
      if (consumer) {
        const peerId = consumer.appData?.producerPeerId as string;
        const kind = consumer.kind as 'audio' | 'video';
        const isShare = consumer.appData?.share as boolean;
        consumer.close();
        this.consumers.delete(consumerId);
        if (isShare && this.onScreenShareStopped) {
          this.onScreenShareStopped(peerId);
        } else if (this.onRemoteStreamRemoved) {
          this.onRemoteStreamRemoved(peerId, kind);
        }
      }
    });

    // Producer closed by another peer
    this.socket.on('producerClosed', ({ peerId }: { producerId: string; peerId: string }) => {
      console.log(`[MediaSoupClient] Producer closed by ${peerId}`);
    });

    // Socket disconnection
    this.socket.on('disconnect', () => {
      console.log('[MediaSoupClient] Disconnected from server');
      if (this.onConnectionStateChange) {
        this.onConnectionStateChange('disconnected');
      }
    });
  }

  /**
   * Creates a new room and joins it
   */
  async createRoom(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!this.socket) {
        return reject(new Error('Socket not connected'));
      }
      
      this.socket.emit('createRoom', async (response: CreateRoomResponse) => {
        if (!response.success) {
          return reject(new Error(response.error));
        }

        this.roomId = response.roomId;
        this.peerId = response.peerId;

        // Initialize mediasoup device with router RTP capabilities
        await this._initializeDevice(response.rtpCapabilities);

        console.log(`[MediaSoupClient] Created room: ${this.roomId}`);
        resolve(this.roomId);
      });
    });
  }

  /**
   * Joins an existing room
   */
  async joinRoom(roomId: string, displayName?: string, token?: string): Promise<Array<{ producerId: string; peerId: string; kind: string; share?: boolean }>> {
    return new Promise((resolve, reject) => {
      if (!this.socket) {
        return reject(new Error('Socket not connected'));
      }

      this.socket.emit('joinRoom', { roomId, displayName, token }, async (response: JoinRoomResponse) => {
        if (!response.success) {
          return reject(new Error(response.error));
        }

        this.roomId = response.roomId;
        this.peerId = response.peerId;

        // Initialize mediasoup device
        await this._initializeDevice(response.rtpCapabilities);

        // Learn the display names of peers already in the room
        (response.existingPeers || []).forEach((p) => {
          if (p.displayName) this.peerNames.set(p.peerId, p.displayName);
        });

        // Merge existing producers returned by server with any producers buffered
        // by 'newProducer' events that arrived before joinRoom completed.
        const existing = response.existingProducers || [];
        this._pendingProducers = [...(this._pendingProducers || []), ...existing];

        console.log(`[MediaSoupClient] Joined room: ${this.roomId}`);
        resolve(this._pendingProducers);
      });
    });
  }

  /**
   * Consumes all pending producers (call after transports are created)
   */
  async consumePendingProducers(): Promise<void> {
    console.log(`[MediaSoupClient] consumePendingProducers called, ${this._pendingProducers.length} pending`);
    if (this._pendingProducers && this._pendingProducers.length > 0) {
      const producers = [...this._pendingProducers];
      this._pendingProducers = [];
      for (const producer of producers) {
        try {
          console.log(`[MediaSoupClient] Consuming pending producer ${producer.producerId} from ${producer.peerId}`);
          await this._consumeProducer(producer.producerId, producer.peerId, producer.share);
        } catch (err) {
          console.error(`[MediaSoupClient] Failed to consume pending producer ${producer.producerId}:`, err);
        }
      }
    }
  }

  /**
   * Initializes the mediasoup-client device
   */
  private async _initializeDevice(rtpCapabilities: RtpCapabilities): Promise<void> {
    this.device = new mediasoupClient.Device();

    await this.device.load({ routerRtpCapabilities: rtpCapabilities });

    console.log('[MediaSoupClient] Device loaded');
  }

  /**
   * Creates send and receive transports
   */
  async createTransports(): Promise<void> {
    console.log('[MediaSoupClient] Creating transports...');
    // Create send transport
    this.sendTransport = await this._createTransport('send');
    console.log('[MediaSoupClient] Send transport created, id:', this.sendTransport.id);

    // Create receive transport
    this.recvTransport = await this._createTransport('recv');
    console.log('[MediaSoupClient] Recv transport created, id:', this.recvTransport.id);

    console.log('[MediaSoupClient] Both transports created successfully');
  }

  /**
   * Creates a single transport (send or receive)
   */
  private async _createTransport(type: 'send' | 'recv'): Promise<Transport> {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.device) {
        return reject(new Error('Socket or device not initialized'));
      }

      this.socket.emit('createTransport', { type }, async (response: CreateTransportResponse) => {
        if (!response.success) {
          return reject(new Error(response.error));
        }

        const { transportParams } = response;
        let transport: Transport;

        if (type === 'send') {
          transport = this.device!.createSendTransport(transportParams);
        } else {
          transport = this.device!.createRecvTransport(transportParams);
        }

        // Handle transport connect event
        transport.on('connect', (
          { dtlsParameters }: { dtlsParameters: DtlsParameters },
          callback: () => void,
          errback: (error: Error) => void
        ) => {
          console.log(`[MediaSoupClient] ${type} transport connect event fired, sending DTLS params to server...`);
          this.socket!.emit('connectTransport', { type, dtlsParameters }, (res: SocketResponse) => {
            if (res.success) {
              console.log(`[MediaSoupClient] ${type} transport connect SUCCESS`);
              callback();
            } else {
              console.error(`[MediaSoupClient] ${type} transport connect FAILED:`, res.error);
              errback(new Error(res.error));
            }
          });
        });

        // Handle produce event (only for send transport)
        if (type === 'send') {
          transport.on('produce', (
            { kind, rtpParameters, appData }: { kind: MediaKind; rtpParameters: RtpParameters; appData: AppData },
            callback: (params: { id: string }) => void,
            errback: (error: Error) => void
          ) => {
            this.socket!.emit('produce', { kind, rtpParameters, appData }, (res: ProduceResponse) => {
              if (res.success) {
                callback({ id: res.producerId });
              } else {
                errback(new Error(res.error));
              }
            });
          });
        }

        // Handle connection state changes
        transport.on('connectionstatechange', (state: string) => {
          console.log(`[MediaSoupClient] ${type} transport state: ${state}`);
          if (this.onConnectionStateChange) {
            this.onConnectionStateChange(state);
          }
        });

        resolve(transport);
      });
    });
  }

  private isIOSSafari(): boolean {
    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    return isIOS && isSafari;
  }

  /**
   * Gets local media stream (audio and/or video)
   */
  async getLocalStream(audio: boolean = true, video: boolean = true): Promise<MediaStream> {
    const isIOS = this.isIOSSafari();
    const videoConstraints: MediaTrackConstraints | boolean = video
      ? isIOS
        ? {
            facingMode: 'user',
            width: { ideal: 1920, min: 1280 },
            height: { ideal: 1080, min: 720 },
            frameRate: { ideal: 30 },
          }
        : {
            width: { ideal: 3840, min: 640 },
            height: { ideal: 2160, min: 480 },
            frameRate: { ideal: 30, min: 15 },
            resizeMode: 'none' as const,
          }
      : false;

    const constraints: MediaStreamConstraints = {
      audio: audio
        ? {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          }
        : false,
      video: videoConstraints,
    };

   
    this.localStream = await navigator.mediaDevices.getUserMedia(constraints);
    const videoTrack = this.localStream.getVideoTracks()[0];
    if (videoTrack) {
      const settings = videoTrack.getSettings();
      console.log(`[MediaSoupClient] Camera resolution: ${settings.width}x${settings.height} @ ${settings.frameRate}fps`);
    }
    
    return this.localStream;
  }

  /**
   * Starts producing audio and/or video
   */
  async produce(stream: MediaStream): Promise<void> {
    if (!this.sendTransport) {
      throw new Error('Send transport not created');
    }

    // Produce audio if available
    const audioTrack = stream.getAudioTracks()[0];
    if (audioTrack) {
      const audioProducer = await this.sendTransport.produce({
        track: audioTrack,
        codecOptions: {
          opusStereo: true,
          opusDtx: true,
        },
      });
      this.producers.set('audio', audioProducer);
      console.log('[MediaSoupClient] Audio producer created');
    }

    // Produce video if available
    const videoTrack = stream.getVideoTracks()[0];
    if (videoTrack) {
      const videoProducer = await this.sendTransport.produce({
        track: videoTrack,
        codecOptions: {
          videoGoogleStartBitrate: 1000,
        },
        encodings: [{ maxBitrate: 100000 }, { maxBitrate: 300000 }, { maxBitrate: 900000 }],
      });
      this.producers.set('video', videoProducer);
      console.log('[MediaSoupClient] Video producer created');
    }
  }

  /**
   * Consumes a remote producer
   */
  private async _consumeProducer(producerId: string, producerPeerId: string, isShare: boolean = false): Promise<Consumer> {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.recvTransport || !this.device) {
        console.error('[MediaSoupClient] _consumeProducer ABORT - missing:', 
          !this.socket ? 'socket' : '', !this.recvTransport ? 'recvTransport' : '', !this.device ? 'device' : '');
        return reject(new Error('Socket, transport, or device not initialized'));
      }

      console.log(`[MediaSoupClient] _consumeProducer: requesting consume for producer=${producerId} from peer=${producerPeerId}`);

      this.socket.emit(
        'consume',
        {
          producerId,
          rtpCapabilities: this.device.rtpCapabilities,
        },
        async (response: ConsumeResponse) => {
          try {
            if (!response.success) {
              console.error('[MediaSoupClient] Server returned consume FAILURE:', response.error);
              return reject(new Error(response.error));
            }

            console.log(`[MediaSoupClient] Server returned consume SUCCESS: consumerId=${response.consumerId}, kind=${response.kind}, producerPeerId=${response.producerPeerId}`);

            const consumer = await this.recvTransport!.consume({
              id: response.consumerId,
              producerId: response.producerId,
              kind: response.kind,
              rtpParameters: response.rtpParameters,
              appData: {
                producerPeerId: response.producerPeerId,
                share: response.share || isShare,
              },
            });

            console.log(`[MediaSoupClient] Consumer created locally: id=${consumer.id}, kind=${consumer.kind}, track.readyState=${consumer.track.readyState}, track.enabled=${consumer.track.enabled}, track.muted=${consumer.track.muted}`);

            this.consumers.set(consumer.id, consumer);

            // Resume the consumer (await it to ensure data flows)
            await new Promise<void>((resumeResolve, resumeReject) => {
              this.socket!.emit('resumeConsumer', { consumerId: consumer.id }, (res: SocketResponse) => {
                if (res.success) {
                  console.log(`[MediaSoupClient] Consumer ${consumer.id} RESUMED successfully`);
                  resumeResolve();
                } else {
                  console.error(`[MediaSoupClient] Consumer ${consumer.id} resume FAILED:`, res.error);
                  resumeReject(new Error(res.error || 'Failed to resume'));
                }
              });
            });

            // Create a MediaStream from the consumer track
            const stream = new MediaStream([consumer.track]);
            console.log(`[MediaSoupClient] MediaStream created: tracks=${stream.getTracks().length}, active=${stream.active}`);
            console.log(`[MediaSoupClient] Track after resume: readyState=${consumer.track.readyState}, enabled=${consumer.track.enabled}, muted=${consumer.track.muted}`);

            // Notify about new remote stream or screen share
            if (response.share || isShare) {
              console.log(`[MediaSoupClient] Calling onScreenShare callback for peer ${producerPeerId}`);
              if (this.onScreenShare) {
                this.onScreenShare(producerPeerId, stream);
              } else {
                console.warn('[MediaSoupClient] onScreenShare callback is NOT set!');
              }
            } else {
              console.log(`[MediaSoupClient] Calling onRemoteStream callback for peer ${producerPeerId}, kind=${consumer.kind}`);
              if (this.onRemoteStream) {
                this.onRemoteStream(producerPeerId, consumer.kind as 'audio' | 'video', stream);
              } else {
                console.warn('[MediaSoupClient] onRemoteStream callback is NOT set!');
              }
            }

            resolve(consumer);
          } catch (err) {
            console.error('[MediaSoupClient] Error in consume callback:', err);
            reject(err);
          }
        }
      );
    });
  }

  /**
   * Toggles audio mute state
   * @returns New mute state (true = muted)
   */
  toggleAudio(): boolean {
    const audioProducer = this.producers.get('audio');
    if (audioProducer) {
      if (audioProducer.paused) {
        audioProducer.resume();
        return false;
      } else {
        audioProducer.pause();
        return true;
      }
    }
    return true;
  }

  /**
   * Toggles video state
   * @returns New state (true = video off)
   */
  toggleVideo(): boolean {
    const videoProducer = this.producers.get('video');
    if (videoProducer) {
      if (videoProducer.paused) {
        videoProducer.resume();
        return false;
      } else {
        videoProducer.pause();
        return true;
      }
    }
    return true;
  }

  /**
   * Sets audio mute state directly
   */
  setAudioMuted(muted: boolean): void {
    const audioProducer = this.producers.get('audio');
    if (audioProducer) {
      if (muted && !audioProducer.paused) {
        audioProducer.pause();
      } else if (!muted && audioProducer.paused) {
        audioProducer.resume();
      }
    }
  }

  /**
   * Sets video state directly
   */
  setVideoOff(off: boolean): void {
    const videoProducer = this.producers.get('video');
    if (videoProducer) {
      if (off && !videoProducer.paused) {
        videoProducer.pause();
      } else if (!off && videoProducer.paused) {
        videoProducer.resume();
      }
    }
  }

  /**
   * Starts screen sharing
   */
  async startScreenShare(): Promise<MediaStream> {
    if (this.screenProducer) {
      console.warn('[MediaSoupClient] Already sharing screen');
      return this.screenStream!;
    }

    if (!this.sendTransport) {
      throw new Error('Send transport not created');
    }

    // Get screen share stream
    this.screenStream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        frameRate: { ideal: 30 },
      },
      audio: false, // Screen audio can be added if needed
    });

    const videoTrack = this.screenStream.getVideoTracks()[0];

    // Handle when user stops sharing via browser UI
    videoTrack.onended = () => {
      this.stopScreenShare();
    };

    // Produce screen share
    this.screenProducer = await this.sendTransport.produce({
      track: videoTrack,
      codecOptions: {
        videoGoogleStartBitrate: 1000,
      },
      encodings: [{ maxBitrate: 500000 }, { maxBitrate: 1000000 }, { maxBitrate: 2500000 }],
      appData: { share: true }, // Mark as screen share
    });

    console.log('[MediaSoupClient] Screen share producer created');
    return this.screenStream;
  }

  /**
   * Stops screen sharing
   */
  async stopScreenShare(): Promise<void> {
    if (this.screenProducer) {
      // Close the producer
      this.screenProducer.close();

      // Notify server
      if (this.socket) {
        this.socket.emit('closeProducer', { producerId: this.screenProducer.id }, () => {});
      }

      this.screenProducer = null;
    }

    if (this.screenStream) {
      this.screenStream.getTracks().forEach((track) => track.stop());
      this.screenStream = null;
    }

    if (this.onScreenShareStopped) {
      this.onScreenShareStopped();
    }

    console.log('[MediaSoupClient] Screen share stopped');
  }

  /**
   * Checks if currently sharing screen
   */
  isScreenSharing(): boolean {
    return !!this.screenProducer && !this.screenProducer.closed;
  }

  /**
   * Gets the local stream
   */
  getStream(): MediaStream | null {
    return this.localStream;
  }

  /**
   * Leaves the current room and cleans up resources
   */
  async leave(): Promise<void> {
    // Close consumers
    this.consumers.forEach((consumer) => consumer.close());
    this.consumers.clear();

    // Close producers
    this.producers.forEach((producer) => producer.close());
    this.producers.clear();

    // Close transports
    if (this.sendTransport) {
      this.sendTransport.close();
      this.sendTransport = null;
    }
    if (this.recvTransport) {
      this.recvTransport.close();
      this.recvTransport = null;
    }

    // Stop local stream
    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => track.stop());
      this.localStream = null;
    }

    // Stop screen share
    if (this.screenStream) {
      this.screenStream.getTracks().forEach((track) => track.stop());
      this.screenStream = null;
    }
    this.screenProducer = null;

    // Notify server
    if (this.socket) {
      this.socket.emit('leaveRoom');
    }

    this.roomId = null;
    this.peerId = null;

    console.log('[MediaSoupClient] Left room and cleaned up');
  }

  /**
   * Disconnects from the server
   */
  disconnect(): void {
    this.leave();
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }
}

export default MediaSoupClient;
